import { ReactNode, useState, useRef, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useSwipeGestures } from '../../hooks/useSwipeGestures';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLanguage } from '../../contexts/LanguageContext';
import type { CalculationMethod } from '../../types';
import type { UserMode } from '../../hooks/useUserMode';

interface AppShellProps {
  children: ReactNode;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  currentPage: string;
  calculationMethod?: CalculationMethod;
  onCalculationMethodChange?: (method: CalculationMethod) => void;
  onShowAlertPanel?: () => void;
  userMode?: UserMode;
  onUserModeChange?: (mode: UserMode) => void;
}

export function AppShell({
  children,
  activeTab,
  onTabChange,
  currentPage,
  calculationMethod,
  onCalculationMethodChange,
  onShowAlertPanel,
  userMode = 'simple',
  onUserModeChange
}: AppShellProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const appMainRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const { language } = useLanguage(); // Force re-render on language change

  // Show calculation selector only on Portfolio page
  const showCalculationSelector = activeTab === 'portfolio';

  // Handle logo click to navigate home (same pattern as sidebar)
  const handleNavigateHome = () => {
    onTabChange('home');
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Swipe gestures on mobile to control sidebar
  useSwipeGestures(appMainRef, {
    onSwipeRight: () => {
      if (isMobile && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    },
    onSwipeLeft: () => {
      if (isMobile && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    }
  });

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Handle body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isMobile, isSidebarOpen]);

  // Handle navigation events from UserMenu dropdown
  useEffect(() => {
    const handleNavigateToSettings = () => {
      onTabChange('settings');
    };
    const handleNavigateToPricing = () => {
      onTabChange('pricing');
    };

    window.addEventListener('navigate-to-settings', handleNavigateToSettings);
    window.addEventListener('navigate-to-pricing', handleNavigateToPricing);

    return () => {
      window.removeEventListener('navigate-to-settings', handleNavigateToSettings);
      window.removeEventListener('navigate-to-pricing', handleNavigateToPricing);
    };
  }, [onTabChange]);

  return (
    <div className="app-shell">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {language === 'fr' ? 'Aller au contenu principal' : 'Skip to main content'}
      </a>

      {/* Mobile sidebar backdrop */}
      {isMobile && (
        <div
          className={`sidebar-backdrop ${isSidebarOpen ? 'visible' : ''}`}
          onClick={handleCloseSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - key forces re-render on language change */}
      <Sidebar
        key={`sidebar-${language}`}
        activeTab={activeTab}
        onTabChange={onTabChange}
        isMobileOpen={isMobile ? isSidebarOpen : false}
        onMobileClose={handleCloseSidebar}
        userMode={userMode}
      />

      {/* Main content */}
      <main id="main-content" className="app-main" ref={appMainRef} tabIndex={-1}>
        {/* Top bar - key forces re-render on language change */}
        <TopBar
          key={`topbar-${language}`}
          currentPage={currentPage}
          calculationMethod={calculationMethod}
          onCalculationMethodChange={onCalculationMethodChange}
          showCalculationSelector={showCalculationSelector}
          onToggleSidebar={isMobile ? handleToggleSidebar : undefined}
          onNavigateHome={handleNavigateHome}
          userMode={userMode}
          onUserModeChange={onUserModeChange}
          onShowAlertPanel={onShowAlertPanel}
        />

        {/* Content with fade-in animation */}
        <div className="app-content animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
